import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Space,
  Table,
  Tag,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';
import { FileGallery, fileList, fmtDate, fmtDay } from 'lib/adminDetail';

const CustomersPage = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/users', {
        params: { type: 'USER', limit: 100 },
      });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((u) =>
      `${u.full_name || ''} ${u.email || ''} ${u.mobile_number || ''} ${u.city || ''} ${u.address || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const showDetail = async (record) => {
    try {
      const res = await axios.get(`/admin/user/${record._id}`);
      setDetail(res?.data || { personalInfo: record });
      setDetailOpen(true);
    } catch (e) {
      message.error('Could not load customer details');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <strong>{v || '—'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.gender || r.dob ? [r.gender, r.dob && fmtDay(r.dob)].filter(Boolean).join(' · ') : ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile_number',
      key: 'mobile_number',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '—',
    },
    {
      title: 'City / address',
      key: 'city',
      render: (_, r) =>
        [r.city, r.address].filter(Boolean).join(' · ') || '—',
    },
    {
      title: 'Phone',
      key: 'phone_verified',
      render: (_, r) =>
        r.is_verified || r.verified_at || r.mobile_number ? (
          <Tag color="green">OTP verified</Tag>
        ) : (
          <Tag>No mobile</Tag>
        ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => fmtDay(v),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            showDetail(record);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const p = detail?.personalInfo;

  return (
    <PageShell
      title="Customers"
      description="App users who collect and redeem coupons. Phone OTP is how customers verify — this is not merchant store KYC."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchCustomers} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Input.Search
          placeholder="Search name, mobile, email, city"
          allowClear
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => showDetail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={p?.full_name || 'Customer details'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Close
          </Button>,
        ]}
        width={720}
      >
        {p && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Name">{p.full_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{p.mobile_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {p.email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Gender">{p.gender || '—'}</Descriptions.Item>
              <Descriptions.Item label="DOB">{fmtDay(p.dob)}</Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {[p.address, p.city, p.state, p.pin_code].filter(Boolean).join(', ') ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {p.is_verified || p.verified_at || p.mobile_number
                  ? 'OTP verified'
                  : 'No mobile'}
              </Descriptions.Item>
              <Descriptions.Item label="Referral code">{p.my_code || '—'}</Descriptions.Item>
              <Descriptions.Item label="Joined">{fmtDate(p.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Updated">{fmtDate(p.updatedAt)}</Descriptions.Item>
              <Descriptions.Item label="Coupons collected">
                {detail.totalCouponCollected ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Redeemed">
                {detail.redeemedCoupon ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Rewards">
                {detail.totalRewardsCollected ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="User id" span={2}>
                {p._id}
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16 }}>
              <FileGallery title="Profile photos" files={fileList(p.images)} />
            </div>
          </>
        )}
      </Modal>
    </PageShell>
  );
};

export default CustomersPage;
